import {useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {importUsers} from '../../../service/authorization';
import {type TypedTranslation} from '../../../i18n';

interface IImportUsersProps {
    setImportUserModal: React.Dispatch<React.SetStateAction<boolean>>;
    setErrorModal: React.Dispatch<React.SetStateAction<boolean>>;
    setSuccessModal: React.Dispatch<React.SetStateAction<boolean>>;
    setErrorMsg: React.Dispatch<React.SetStateAction<string>>;
    setSuccessMsg: React.Dispatch<React.SetStateAction<string>>;
}

interface ErrorResponse {
    response?: {
        data?: {
            message?: TypedTranslation;
            users?: string[];
        };
    };
}

// eslint-disable-next-line
function isErrorResponse(error: any): error is ErrorResponse {
    return error && error.response && error.response.data && typeof error.response.data.message === 'string';
}

export const useImportUsers = ({
    setImportUserModal,
    setSuccessModal,
    setErrorModal,
    setErrorMsg,
    setSuccessMsg,
}: IImportUsersProps): UseMutationResult<number, unknown, File> => {
    const queryClient = useQueryClient();
    const {t} = useTranslation();

    return useMutation({
        mutationFn: importUsers,
        onSuccess: (res) => {
            setImportUserModal(false);
            setSuccessModal(true);
            setSuccessMsg(t('registerPage.importUsers.success.description', {users: res}));
            queryClient.invalidateQueries({queryKey: QUERY_KEYS.user.lists()});
        },
        onError: (error) => {
            setImportUserModal(false);
            setErrorModal(true);

            if (isErrorResponse(error) && error.response?.data?.message) {
                setErrorMsg(t(error?.response?.data?.message));
            }
            if (isErrorResponse(error) && error.response?.data?.message && error.response?.data?.users?.length) {
                const {users} = error.response.data;
                let usersMsg: string;
                if (users.length <= 3) {
                    usersMsg = users.join(', ');
                } else {
                    usersMsg = `${users.slice(0, 3).join(', ')} and ${users.length - 3} more`;
                }
                setErrorMsg(`${t(error.response.data.message)}: ${usersMsg}`);
            }
        },
    });
};
